'use client';

import * as React from 'react';
import { format } from 'date-fns';
import {
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieLabelRenderProps,
} from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDashboardChartData,
  ChartFilters,
} from '@/hooks/useDashboardChartData';

/**
 * DashboardCharts Component
 *
 * Displays two charts for dashboard analytics:
 * 1. Job Status Breakdown (PieChart)
 * 2. Inspections Over Time (LineChart)
 *
 * Features:
 * - Responsive design (300px mobile, 400px desktop)
 * - Loading states with Skeleton components
 * - Error handling with error messages
 * - Optional filters for date range and status
 * - Uses design system colors and Card components
 *
 * @param filters - Optional filters for date range and status
 *
 * @example
 * ```tsx
 * // Basic usage
 * <DashboardCharts />
 *
 * // With filters
 * <DashboardCharts
 *   filters={{
 *     dateRange: {
 *       startDate: '2025-01-01',
 *       endDate: '2025-01-31',
 *     },
 *     status: ['pending', 'in-progress'],
 *   }}
 * />
 * ```
 */
export interface DashboardChartsProps {
  filters?: ChartFilters;
}

export function DashboardCharts({ filters }: DashboardChartsProps) {
  const { data, isLoading, error } = useDashboardChartData(filters);

  if (isLoading) {
    return <DashboardChartsLoading />;
  }

  if (error) {
    return <DashboardChartsError error={error} />;
  }

  if (!data) {
    return <DashboardChartsError error={new Error('No data available')} />;
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Job Status Breakdown - PieChart */}
      <Card>
        <CardHeader>
          <CardTitle>Job Status Breakdown</CardTitle>
          <CardDescription>Distribution of jobs by current status</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300} className="md:h-[400px]">
            <PieChart>
              <Pie
                data={data.jobStatusBreakdown}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.jobStatusBreakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                formatter={(value) => (
                  <span className="text-sm text-text-primary">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Inspections Over Time - LineChart */}
      <Card>
        <CardHeader>
          <CardTitle>Inspections Over Time</CardTitle>
          <CardDescription>Daily inspection trends for the past week</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300} className="md:h-[400px]">
            <LineChart
              data={data.inspectionsOverTime}
              margin={{
                top: 5,
                right: 30,
                left: 0,
                bottom: 5,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e5e7eb"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => {
                  try {
                    return format(new Date(value), 'MMM dd');
                  } catch {
                    return value;
                  }
                }}
                tick={{ fill: '#6b7280', fontSize: 12 }}
                tickLine={{ stroke: '#d1d5db' }}
              />
              <YAxis
                tick={{ fill: '#6b7280', fontSize: 12 }}
                tickLine={{ stroke: '#d1d5db' }}
                axisLine={{ stroke: '#d1d5db' }}
              />
              <Tooltip content={<CustomLineTooltip />} />
              <Line
                type="monotone"
                dataKey="inspections"
                stroke="rgb(var(--color-primary-500))"
                strokeWidth={2}
                dot={{
                  fill: 'rgb(var(--color-primary-500))',
                  strokeWidth: 2,
                  r: 4,
                }}
                activeDot={{
                  r: 6,
                  fill: 'rgb(var(--color-primary-500))',
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Custom label renderer for PieChart
 */
const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: PieLabelRenderProps) => {
  const numCx = Number(cx);
  const numCy = Number(cy);
  const numMidAngle = Number(midAngle);
  const numInnerRadius = Number(innerRadius);
  const numOuterRadius = Number(outerRadius);
  const numPercent = Number(percent);
  const RADIAN = Math.PI / 180;
  const radius = numInnerRadius + (numOuterRadius - numInnerRadius) * 0.5;
  const x = numCx + radius * Math.cos(-numMidAngle * RADIAN);
  const y = numCy + radius * Math.sin(-numMidAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > numCx ? 'start' : 'end'}
      dominantBaseline="central"
      className="text-xs font-semibold"
    >
      {`${(numPercent * 100).toFixed(0)}%`}
    </text>
  );
};

/**
 * Custom Tooltip for PieChart
 */
const CustomPieTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{name: string; value: number; payload: {color: string}}> }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    if (!data) return null;
    return (
      <div className="rounded-lg border border-border-primary bg-background-primary p-3 shadow-lg">
        <p className="text-sm font-medium text-text-primary">{data.name}</p>
        <p className="text-lg font-bold" style={{ color: data.payload.color }}>
          {data.value} jobs
        </p>
      </div>
    );
  }
  return null;
};

/**
 * Custom Tooltip for LineChart
 */
const CustomLineTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{value: number}>; label?: string }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    if (!data) return null;
    let formattedDate = label;

    try {
      formattedDate = format(new Date(label ?? ''), 'MMM dd, yyyy');
    } catch {
      // Use label as-is if date parsing fails
    }

    return (
      <div className="rounded-lg border border-border-primary bg-background-primary p-3 shadow-lg">
        <p className="text-sm font-medium text-text-secondary">{formattedDate}</p>
        <p className="text-lg font-bold text-primary-500">
          {data.value} inspections
        </p>
      </div>
    );
  }
  return null;
};

/**
 * Loading State Component
 */
function DashboardChartsLoading() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* PieChart Skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center">
            <Skeleton className="h-[300px] w-[300px] rounded-full md:h-[400px] md:w-[400px]" />
          </div>
        </CardContent>
      </Card>

      {/* LineChart Skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full md:h-[400px]" />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Error State Component
 */
function DashboardChartsError({ error }: { error: Error }) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Job Status Breakdown</CardTitle>
          <CardDescription>Distribution of jobs by current status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-[300px] items-center justify-center md:h-[400px]">
            <div className="text-center">
              <p className="text-sm font-medium text-error-500">
                Failed to load chart data
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                {(error instanceof Error ? error.message : String(error)) || 'An unexpected error occurred'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Inspections Over Time</CardTitle>
          <CardDescription>Daily inspection trends for the past week</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-[300px] items-center justify-center md:h-[400px]">
            <div className="text-center">
              <p className="text-sm font-medium text-error-500">
                Failed to load chart data
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                {(error instanceof Error ? error.message : String(error)) || 'An unexpected error occurred'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
